/**
 * `wayai triggers …` — what a base does when a record changes: call a webhook,
 * patch a referenced record, or mirror the write upstream. Top-level
 * (docs/reference/rekor-platform-contracts.md §3.2).
 */

import { randomUUID } from 'node:crypto';
import { Command } from 'commander';
import { expected } from '../../lib/expected.js';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import { outputFormat, parseData, pathSegment, readSecret, requireBase, splitList } from '../helpers.js';
import { confirm } from '../../lib/utils.js';

export function buildTriggersCommand(): Command {
  const triggers = new Command('triggers').description(
    'Manage triggers (outbound webhooks, internal writes, external writes)',
  );

  triggers
    .command('create')
    .description(
      'Create a trigger (a webhook, an internal_write that patches a referenced record, or an external_write that mirrors the record to an external source)',
    )
    .requiredOption('--name <name>', 'Trigger name')
    .requiredOption('--events <events>', 'Comma-separated event types')
    .option('--url <url>', 'Webhook target URL (webhook actions)')
    // NOT `--secret <value>` — see `readSecret`: an argv element leaks through
    // /proc, shell history and CI logs.
    .option('--secret-stdin', 'Read the webhook HMAC signing secret from stdin (webhook actions)')
    .option('--secret-prompt', 'Prompt for the webhook HMAC signing secret, masked (webhook actions)')
    .option(
      '--action <json>',
      'Action JSON. webhook {"type":"webhook","url":...,"secret":...}; internal_write {"type":"internal_write","target":"slots","match":{"source_field":"data.slot_id"},"patch":{"status":"busy"},"precondition":{"field":"data.status","op":"eq","value":"free"},"mode":"async"} (mode: "async" applies after the write, "transactional" applies atomically so a precondition miss rolls the write back); external_write {"type":"external_write","record_type":"upstream_appointments","source":"legacy","op":"create","watched_fields":"mapped"} (writes the triggering record through that source\'s write path — translation, endpoint, auth — and links the upstream-assigned id back as external_id; "record_type" defaults to the triggering record_type, "op" defaults from the event; "watched_fields" scopes UPDATE dispatch to writes that changed an upstream-mapped field — "mapped" = the source\'s to_external keys, or an explicit array of data fields — so an overlay-only write skips the redundant upstream push; omit to fire on every update)',
    )
    .option('--id <id>', 'Trigger ID (auto-generated if omitted)')
    .option('--record-type-scope <record_types>', 'Comma-separated record_type scope')
    .option('--filter <json>', 'Filter expression (JSON)')
    .option(
      '--skip-cascade-writes <bool>',
      'Skip firing on cascade-originated (internal_write) writes (default true)',
    )
    .action(async function (
      this: Command,
      opts: {
        name: string;
        url?: string;
        secretStdin?: boolean;
        secretPrompt?: boolean;
        action?: string;
        events: string;
        id?: string;
        recordTypeScope?: string;
        filter?: string;
        skipCascadeWrites?: string;
      },
    ) {
      if (!opts.action && !opts.url) {
        throw expected('Provide --url (webhook) or --action <json> (e.g. an internal_write action).');
      }
      const body: Record<string, unknown> = {
        name: opts.name,
        events: splitList(opts.events),
        enabled: true,
      };
      if (opts.action) {
        body.action = parseData(opts.action);
      } else {
        // Legacy flat shape — normalized into a webhook action server-side.
        body.url = opts.url;
        if (opts.secretStdin || opts.secretPrompt) {
        body.secret = await readSecret(
          {
            stdin: opts.secretStdin,
            prompt: opts.secretPrompt,
            stdinFlag: '--secret-stdin',
            promptHint: '--secret-prompt',
          },
          'Webhook signing secret',
        );
      }
      }
      if (opts.recordTypeScope) body.record_type_scope = splitList(opts.recordTypeScope);
      if (opts.filter) body.filter = parseData(opts.filter);
      if (opts.skipCascadeWrites !== undefined) {
        body.skip_cascade_writes = opts.skipCascadeWrites !== 'false';
      }

      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const triggerId = opts.id ?? randomUUID();
      printOutput(
        await client.request('PUT', `/v1/${base}/triggers/${pathSegment(triggerId, '--id')}`, body),
        outputFormat(this),
      );
    });

  triggers
    .command('get <id>')
    .description('Get a trigger')
    .action(async function (this: Command, id: string) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(await client.request('GET', `/v1/${base}/triggers/${pathSegment(id, 'trigger id')}`), outputFormat(this));
    });

  triggers
    .command('list')
    .description('List all triggers')
    .action(async function (this: Command) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(await client.request('GET', `/v1/${base}/triggers`), outputFormat(this));
    });

  triggers
    .command('deliveries')
    .description('List trigger delivery attempts (status, retries, errors)')
    .option('--status <status>', 'Filter by status (pending | delivered | failed | dead)')
    .option('--trigger-id <id>', 'Filter by trigger id')
    .action(async function (this: Command, opts: { status?: string; triggerId?: string }) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const params = new URLSearchParams();
      if (opts.status) params.set('status', opts.status);
      if (opts.triggerId) params.set('trigger_id', opts.triggerId);
      const qs = params.toString();
      printOutput(
        await client.request('GET', `/v1/${base}/triggers/deliveries${qs ? `?${qs}` : ''}`),
        outputFormat(this),
      );
    });

  triggers
    .command('delete <id>')
    .description('Delete a trigger')
    .option('-y, --yes', 'Skip confirmation prompt')
    .action(async function (this: Command, id: string, opts: { yes?: boolean }) {
      if (!opts.yes && !(await confirm(`Delete trigger ${id}? This cannot be undone.`))) {
        console.log('Aborted');
        return;
      }
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      await client.request('DELETE', `/v1/${base}/triggers/${pathSegment(id, 'trigger id')}`);
      console.log('Deleted');
    });

  return triggers;
}
